import React from "react";
import { apiConstants } from "../../../common/constants";
import { getOrderStatus } from "../../../helpers/UtilityHelper";
import { useGenericPostRequestQuery, useGetPaginatedShipmentsQuery } from "../../../redux/commonRTK";
import _ from 'lodash'
import WhatsaAppContext from "./Context";
import getRequestFilter, { NDR_TEMPLATE_KEY } from "./filter";

// Translate the in-table filter form values into server-side `andfilter` clauses
// so paging stays correct (client-side filtering can only see the current page).
export function buildExtraFilters({ awbNumber, isconversationCompleted } = {}) {
    const extra = []
    if (awbNumber) extra.push({ field: 'meta_data.awbNumber', operator: 'eq', value: awbNumber })
    // `isconversationCompleted` is set to `true` only when a conversation actually completes;
    // on in-progress / no-reply conversations the field is simply ABSENT (not stored as false).
    // So "Yes" = eq true, but "No" must be `ne true` — Mongo's $ne matches both `false` AND
    // missing, whereas `eq false` would drop every absent-field doc and return almost nothing.
    if (isconversationCompleted === 'Yes') {
        extra.push({ field: 'isconversationCompleted', operator: 'eq', value: true })
    } else if (isconversationCompleted === 'No') {
        extra.push({ field: 'isconversationCompleted', operator: 'ne', value: true })
    }
    return extra
}

export default function useNdr({ page = 0, sizePerPage = 20, filters } = {}) {
    const { dateRange } = React.useContext(WhatsaAppContext)

    // Server-side pagination: request only the current page. `count` comes back on
    // the first page (skipCount=false) and is reused on later pages (skipCount=true).
    const requestPayload = getRequestFilter(dateRange, NDR_TEMPLATE_KEY, {
        limit: sizePerPage,
        offset: page * sizePerPage,
        skipCount: page > 0,
        extraFilters: buildExtraFilters(filters),
    })

    const { data: responseData, isLoading, isFetching } = useGenericPostRequestQuery({ url: apiConstants.DELIVERY_DB_SEARCH, data: requestPayload, type: 'POST' })

    // Keep the last known total so pages that skip the count still report it.
    const countRef = React.useRef(0)
    if (responseData?.count != null) countRef.current = responseData.count

    const skip = isLoading || isFetching
    const awbNumbers = _(responseData?.data || []).map((item) => item?.meta_data?.awbNumber || null).filter((i) => i).uniq().value()
    const orderSearchQueryPayload = {
        limit: 2147483647,
        andfilter: [
            { field: 'awbNumber', operator: 'in', value: awbNumbers }
        ],
        includes: 'id,awbNumber,orderStatus,courierDetail,paymentMode'
    }
    const { data: allShipments, isLoading: isLoadingShipments, isFetching: isFetchingShipments } = useGetPaginatedShipmentsQuery(orderSearchQueryPayload, { skip })
    if (isLoading || isFetching || isFetchingShipments || isLoadingShipments) return { isLoading: true }

    const { data } = responseData
    const { data: allShipmentData } = allShipments
    const result = { isLoading: false, totalSent: countRef.current }

    const groupedByAwbNumber = _(data)
        // .groupBy('meta_data.awbNumber')
        .map(function (items) {
            const awbNumber = items.meta_data.awbNumber
            const id = items.identifier
            const isconversationCompleted = items?.isconversationCompleted
            const row = {id, awbNumber, totalConversations: items.length, conversations: [items],isconversationCompleted }
            const shipment = _.find(allShipmentData, { awb_number: awbNumber })

            if (shipment) {
                row.currentStatus = getOrderStatus(shipment.orderStatus)
                // Show the PARENT courier name (e.g. "Delhivery") rather than the account/service
                // name (e.g. "Delhivery HEAVY"), matching the admin report. Fall back to name.
                row.courierPartner = shipment.courierDetail?.parent || shipment.courierDetail?.name
                row.paymentMode = shipment.payment_mode
            }
            return row;
        }).value()
    return { ...result, data, groupedByAwbNumber };
}